// Minimal iCalendar parser for browser-side calendar import.
//
// Scope: extract VEVENT entries with start/end + summary, plus simple weekly
// or daily RRULE expansion within a given date range. We deliberately do NOT
// handle every RFC-5545 corner case — it's enough to show "you are busy here".
//
// All parsing happens in the user's browser. This module never makes network
// calls and never stores anything. The user pastes ICS text in, busy-time
// ranges come out.
//
// Hardening (siehe security.html §6):
//  - Maximal MAX_EVENTS Events pro Datei (Parser bricht danach ab).
//  - SUMMARY hart auf MAX_SUMMARY Zeichen geschnitten.
//  - LOCATION/DESCRIPTION werden ignoriert (nicht extrahiert).
//  - Recurring Events werden auf max. 1 Jahr expandiert (siehe expandRecurring).

const MAX_EVENTS = 5000;
const MAX_SUMMARY = 80;

export type BusyEvent = {
  /** Local Date (we ignore TZID and treat times as local for the user). */
  start: Date;
  end: Date;
  /** Optional summary, only used in tooltips. May be empty. */
  summary: string;
  /** All-day event (DTSTART;VALUE=DATE). End is exclusive end-of-day. */
  isAllDay: boolean;
  /** Source event had RRULE (for UI hint that recurring rules may not be fully expanded). */
  isRecurring: boolean;
};

/**
 * Parse ICS text and return all events overlapping the given date range.
 * Range bounds are inclusive on the start side and exclusive on the end.
 *
 * @param icsText  raw ICS file content
 * @param rangeStart yyyy-mm-dd local
 * @param rangeEnd   yyyy-mm-dd local (inclusive day; we extend to end-of-day)
 */
export function parseICS(icsText: string, rangeStart: string, rangeEnd: string): BusyEvent[] {
  const rangeStartMs = parseLocalDate(rangeStart).getTime();
  const rangeEndMs = endOfDayLocal(rangeEnd).getTime();

  const lines = unfoldLines(icsText);
  const out: BusyEvent[] = [];

  let inEvent = false;
  let dtstart: Date | null = null;
  let dtend: Date | null = null;
  let summary = '';
  let isAllDay = false;
  let rrule = '';
  let exdates: number[] = [];

  for (const line of lines) {
    if (line === 'BEGIN:VEVENT') {
      inEvent = true;
      dtstart = null;
      dtend = null;
      summary = '';
      isAllDay = false;
      rrule = '';
      exdates = [];
      continue;
    }
    if (line === 'END:VEVENT') {
      inEvent = false;
      if (dtstart && dtend) {
        const base: BusyEvent = {
          start: dtstart,
          end: dtend,
          summary: summary.length > MAX_SUMMARY ? summary.substring(0, MAX_SUMMARY) : summary,
          isAllDay,
          isRecurring: rrule !== '',
        };
        const expanded = rrule
          ? expandRecurring(base, rrule, exdates, rangeStartMs, rangeEndMs)
          : [base];
        for (const ev of expanded) {
          if (ev.end.getTime() <= rangeStartMs) continue;
          if (ev.start.getTime() >= rangeEndMs) continue;
          out.push(ev);
          if (out.length >= MAX_EVENTS) {
            // Harter Stop. Bei dieser Anzahl ist eh die UI nicht mehr handhabbar.
            return out;
          }
        }
      }
      continue;
    }
    if (!inEvent) continue;

    const colonIdx = line.indexOf(':');
    if (colonIdx < 0) continue;
    const left = line.substring(0, colonIdx);
    const value = line.substring(colonIdx + 1);
    const semicolonIdx = left.indexOf(';');
    const propName = (semicolonIdx < 0 ? left : left.substring(0, semicolonIdx)).toUpperCase();
    const params = semicolonIdx < 0 ? '' : left.substring(semicolonIdx + 1).toUpperCase();

    switch (propName) {
      case 'DTSTART': {
        const allDay = params.includes('VALUE=DATE');
        dtstart = parseDateTime(value, allDay);
        if (allDay) isAllDay = true;
        break;
      }
      case 'DTEND': {
        const allDay = params.includes('VALUE=DATE');
        dtend = parseDateTime(value, allDay);
        break;
      }
      case 'DURATION': {
        // ISO duration like PT1H30M; we only handle the common cases.
        if (dtstart) dtend = new Date(dtstart.getTime() + parseDurationToMs(value));
        break;
      }
      case 'SUMMARY':
        summary = decodeText(value);
        break;
      case 'RRULE':
        rrule = value;
        break;
      case 'EXDATE': {
        const allDay = params.includes('VALUE=DATE');
        for (const part of value.split(',')) {
          const d = parseDateTime(part, allDay);
          if (!Number.isNaN(d.getTime())) exdates.push(d.getTime());
        }
        break;
      }
    }
  }

  return out;
}

/**
 * Unfold RFC-5545 line continuations (a leading space/tab on a line means
 * "this line continues the previous one"). Strips empty lines.
 */
function unfoldLines(text: string): string[] {
  const raw = text.split(/\r?\n/);
  const result: string[] = [];
  for (const line of raw) {
    if (line.length === 0) continue;
    if ((line.startsWith(' ') || line.startsWith('\t')) && result.length > 0) {
      result[result.length - 1] += line.substring(1);
    } else {
      result.push(line);
    }
  }
  return result;
}

function parseDateTime(value: string, allDay: boolean): Date {
  // VALUE=DATE: yyyymmdd
  if (allDay && /^\d{8}$/.test(value)) {
    return new Date(
      Number(value.substring(0, 4)),
      Number(value.substring(4, 6)) - 1,
      Number(value.substring(6, 8)),
      0, 0, 0,
    );
  }
  // yyyymmddThhmmss[Z]
  const isUTC = value.endsWith('Z');
  const v = isUTC ? value.substring(0, value.length - 1) : value;
  const m = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})$/.exec(v);
  if (!m) return new Date(NaN);
  const [, y, mo, d, h, mi, s] = m;
  if (isUTC) {
    return new Date(Date.UTC(+y, +mo - 1, +d, +h, +mi, +s));
  }
  return new Date(+y, +mo - 1, +d, +h, +mi, +s);
}

function parseDurationToMs(s: string): number {
  // P[n]DT[n]H[n]M[n]S — handle days, hours, minutes, seconds.
  const m = /^P(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?)?$/.exec(s);
  if (!m) return 0;
  const [, d, h, mi, sec] = m;
  return (
    (Number(d ?? 0) * 86400 +
      Number(h ?? 0) * 3600 +
      Number(mi ?? 0) * 60 +
      Number(sec ?? 0)) * 1000
  );
}

function decodeText(s: string): string {
  return s
    .replace(/\\n/gi, ' ')
    .replace(/\\,/g, ',')
    .replace(/\\;/g, ';')
    .replace(/\\\\/g, '\\');
}

function parseLocalDate(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d, 0, 0, 0);
}

function endOfDayLocal(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d, 23, 59, 59, 999);
}

// ---------- RRULE expansion ----------

/**
 * Day-of-week offsets relative to the START of an ISO week (Monday).
 * Used in BYDAY expansion: weekStart + offset = the actual occurrence date.
 */
const ISO_DAY_OFFSET: Record<string, number> = {
  MO: 0, TU: 1, WE: 2, TH: 3, FR: 4, SA: 5, SU: 6,
};

function parseRRule(rule: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const part of rule.split(';')) {
    const eq = part.indexOf('=');
    if (eq < 0) continue;
    out[part.substring(0, eq).toUpperCase()] = part.substring(eq + 1);
  }
  return out;
}

/**
 * Expand a recurring event in our date range. We support FREQ=DAILY and
 * FREQ=WEEKLY (with optional BYDAY). Other frequencies fall back to the base
 * occurrence only — the UI marks them so the user knows there might be more.
 */
function expandRecurring(
  base: BusyEvent,
  rrule: string,
  exdates: number[],
  rangeStartMs: number,
  rangeEndMs: number,
): BusyEvent[] {
  const params = parseRRule(rrule);
  const freq = params.FREQ;
  const interval = Number(params.INTERVAL ?? '1') || 1;
  const count = params.COUNT ? Number(params.COUNT) : Infinity;
  const until = params.UNTIL ? parseDateTime(params.UNTIL, false).getTime() : rangeEndMs;
  const stopAt = Math.min(until, rangeEndMs);

  if (freq !== 'WEEKLY' && freq !== 'DAILY') {
    // We don't expand monthly/yearly. Return the base event so the user at
    // least sees the first instance.
    return [base];
  }

  const duration = base.end.getTime() - base.start.getTime();
  const exclude = new Set(exdates);
  const out: BusyEvent[] = [];

  let produced = 0;
  const stepDays = freq === 'DAILY' ? interval : 7 * interval;
  const byDay = params.BYDAY ? params.BYDAY.split(',') : null;

  let cursor = new Date(base.start);
  // Hard cap for safety: avoid infinite loops if data is malformed.
  let safetyLimit = 5000;

  while (cursor.getTime() <= stopAt && produced < count && safetyLimit-- > 0) {
    if (freq === 'WEEKLY' && byDay) {
      // For weekly with BYDAY, expand each day of the cursor's week.
      const weekStart = startOfWeek(cursor);
      for (const dayCode of byDay) {
        const offset = ISO_DAY_OFFSET[dayCode.replace(/^[+-]?\d+/, '').toUpperCase()];
        if (offset === undefined) continue;
        const inst = new Date(weekStart);
        inst.setDate(weekStart.getDate() + offset);
        inst.setHours(cursor.getHours(), cursor.getMinutes(), cursor.getSeconds(), 0);
        if (inst.getTime() < base.start.getTime()) continue;
        if (inst.getTime() > stopAt) break;
        if (exclude.has(inst.getTime())) continue;
        out.push({
          ...base,
          start: new Date(inst),
          end: new Date(inst.getTime() + duration),
        });
        produced++;
        if (produced >= count) break;
      }
      cursor.setDate(cursor.getDate() + stepDays);
    } else {
      if (cursor.getTime() >= rangeStartMs - duration && !exclude.has(cursor.getTime())) {
        out.push({
          ...base,
          start: new Date(cursor),
          end: new Date(cursor.getTime() + duration),
        });
      }
      produced++;
      cursor.setDate(cursor.getDate() + stepDays);
    }
  }

  return out;
}

function startOfWeek(d: Date): Date {
  // ISO week starts Monday. Offset accordingly.
  const day = d.getDay(); // 0 = Sun
  const offset = (day + 6) % 7; // 0 = Mon, 6 = Sun
  const start = new Date(d);
  start.setDate(d.getDate() - offset);
  start.setHours(0, 0, 0, 0);
  return start;
}
